/**
 * © 2025 Little Shilling, Inc.
 * Shon Little
 * Created: 2025-01-23
 */

import { StudioRole } from './studio';

// Member status enumeration
export enum MemberStatus {
  ACTIVE = 'active',
  INACTIVE = 'inactive',
  PENDING = 'pending',
  SUSPENDED = 'suspended',
}

// Studio member interface
export interface StudioMember {
  id: string;
  user: {
    id: string;
    first_name: string;
    last_name: string;
    full_name: string;
    email: string;
    phone_number?: string;
    avatar_url?: string;
    has_avatar: boolean;
    last_login?: Date;
    profile?: {
      bio?: string;
      title?: string;
      company?: string;
      website?: string;
      linkedin?: string;
      instagram?: string;
      facebook?: string;
      address_visible: boolean;
      email_visible: boolean;
      phone_visible: boolean;
    };
  } | null; // User can be null for pending invitations
  email: string; // Direct email field for pending invitations
  role: StudioRole;
  status: MemberStatus;
  joined_at: Date;
  activated_at?: Date;
  display_name: string;
  display_email: string;
  is_pending_invitation: boolean;
  avatar_url?: string;
  has_avatar: boolean;
  studio_name?: string;
  invited_by_name?: string;
  invitation_expires_at?: Date;
  is_invitation_valid?: boolean;
  can_manage_studio?: boolean;
  can_manage_members?: boolean;
  can_manage_classes?: boolean;
  permissions?: string[];
}

// Parameters for fetching studio members
export interface StudioMembersParams {
  studioId: string;
  search?: string;
  role?: StudioRole;
  status?: MemberStatus;
  page?: number;
  pageSize?: number;
}

// Role change request
export interface RoleChangeRequest {
  membershipId: string;
  role: StudioRole;
  reason?: string;
}

// Remove member request
export interface RemoveMemberRequest {
  membershipId: string;
  reason?: string;
}

// Member action types
export enum MemberAction {
  CHANGE_ROLE = 'change_role',
  REMOVE_MEMBER = 'remove_member',
  VIEW_PROFILE = 'view_profile',
  SEND_MESSAGE = 'send_message',
}

// Component prop interfaces
export interface StudioMembersTabProps {
  studioId: string;
}

export interface MemberListProps {
  members: StudioMember[];
  loading?: boolean;
  onRoleChange: (
    membershipId: string,
    role: StudioRole,
    reason?: string
  ) => void;
  onRemoveMember: (membershipId: string, reason?: string) => void;
  onViewProfile: (userId: string) => void;
  onSendMessage: (userId: string) => void;
}

export interface MemberCardProps {
  member: StudioMember;
  onRoleChange: (
    membershipId: string,
    role: StudioRole,
    reason?: string
  ) => void;
  onRemoveMember: (membershipId: string, reason?: string) => void;
  onViewProfile: (userId: string) => void;
  onSendMessage: (userId: string) => void;
  currentUserRole?: StudioRole;
}

export interface MemberActionMenuProps {
  member: StudioMember;
  onRoleChange: (role: StudioRole, reason?: string) => void;
  onRemoveMember: (reason?: string) => void;
  onViewProfile: () => void;
  onSendMessage: () => void;
  currentUserRole?: StudioRole;
}

export interface MemberSearchBarProps {
  searchTerm: string;
  selectedRole?: StudioRole;
  selectedStatus?: MemberStatus;
  onSearchChange: (search: string) => void;
  onRoleFilter: (role?: StudioRole) => void;
  onStatusFilter: (status?: MemberStatus) => void;
  onClearFilters: () => void;
}

// Role change dialog props
export interface RoleChangeDialogProps {
  open: boolean;
  member: StudioMember;
  currentUserRole?: StudioRole;
  onConfirm: (role: StudioRole, reason?: string) => void;
  onCancel: () => void;
}

// Remove member dialog props
export interface RemoveMemberDialogProps {
  open: boolean;
  member: StudioMember;
  onConfirm: (reason?: string) => void;
  onCancel: () => void;
}
